'use client'

export type OmiziUserType = 'internal' | 'external'
export type OmiziTokenType = 'user' | 'workload'

export interface OmiziAuthContext {
  subject: string
  issuer: string
  audience: string | string[]
  azureTenantId: string
  azureObjectId?: string
  clientApplicationId?: string
  userType?: OmiziUserType
  omiziTenantId?: string
  omiziBusinessId?: string
  groupIds: string[]
  appRoles: string[]
  permissions: string[]
  displayName?: string
  username?: string
  tokenType: OmiziTokenType
}

export interface OmiziPortalUser {
  id: string
  uuid?: string
  email: string
  userType?: OmiziUserType
  firstName?: string
  lastName?: string
  role?: string
  displayName?: string
  tenantId?: string
  businessId?: string
  azureTenantId?: string
  azureObjectId?: string
  clientApplicationId?: string
  groupIds: string[]
  appRoles: string[]
  permissions: string[]
  tokenType: OmiziTokenType
  username?: string
  imageUrl?: string
  publicMetadata?: Record<string, unknown>
  emailAddresses?: Array<{ emailAddress: string }>
  phoneNumbers?: Array<{ phoneNumber: string }>
  twoFactorEnabled?: boolean
}

export interface OmiziAuthSession {
  accessToken: string
  authContext: OmiziAuthContext
  user: OmiziPortalUser
  permissions: string[]
  fetchedAt: string
}

export const OMIZI_AUTH_SESSION_STORAGE_KEY = 'omizi.azure.auth.session'

function getWindowStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function safeParse<T>(value: string | null): T | null {
  if (!value) {
    return null
  }

  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

export function createEmptyAuthContext(): OmiziAuthContext {
  return {
    subject: '',
    issuer: '',
    audience: '',
    azureTenantId: '',
    groupIds: [],
    appRoles: [],
    permissions: [],
    tokenType: 'user',
  }
}

export function createEmptyPortalUser(): OmiziPortalUser {
  return {
    id: '',
    email: '',
    groupIds: [],
    appRoles: [],
    permissions: [],
    tokenType: 'user',
  }
}

export function readStoredAuthSession(): OmiziAuthSession | null {
  const storage = getWindowStorage()
  if (!storage) {
    return null
  }

  return safeParse<OmiziAuthSession>(storage.getItem(OMIZI_AUTH_SESSION_STORAGE_KEY))
}

export function writeStoredAuthSession(session: OmiziAuthSession): void {
  const storage = getWindowStorage()
  if (!storage) {
    return
  }

  storage.setItem(OMIZI_AUTH_SESSION_STORAGE_KEY, JSON.stringify(session))
}

export function clearStoredAuthSession(): void {
  const storage = getWindowStorage()
  if (!storage) {
    return
  }

  storage.removeItem(OMIZI_AUTH_SESSION_STORAGE_KEY)
}

export function normalizeAuthErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }

  if (typeof error === 'string' && error.trim()) {
    return error
  }

  return fallback
}

export function subscribeToStoredAuthSessionChange(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === OMIZI_AUTH_SESSION_STORAGE_KEY) {
      listener()
    }
  }

  window.addEventListener('storage', onStorage)

  return () => {
    window.removeEventListener('storage', onStorage)
  }
}
